/* eslint-disable react/prop-types */
import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import LeadGenerationContext from '../../context/LeadGenerationContext';

function fetchCandidate(url) {
  return fetch(url, { cache: 'no-store' })
    .then((res) => ({ url, res }))
    .catch((err) => ({ url, err }));
}

function readCandidateText(item) {
  if (item.res && item.res?.ok) {
    return item.res.text().then((text) => ({ url: item.url, text }));
  }
  return Promise.resolve({ url: item.url, text: null });
}

function DummyPageInternal(props) {
  const elRef = useRef(null);
  const location = useLocation();

  const { productName, language, productType, accessToken } = props;
  const { initiateUiResponse } = useContext(LeadGenerationContext);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const candidates = [
      '/wc-DummyInitializer.js',
      '/dist/wc-DummyInitializer.js',
      '/static/js/wc-DummyInitializer.js',
      'dist/wc-DummyInitializer.js',
    ];

    // NEW: inject by URL only (no Blob, so no `blob:` scheme)
    function injectScriptSrc(url) {
      return new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = url;
        s.defer = true; // keep defer; set to false if you need immediate execution
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
      });
    }

    async function tryLoad() {
      try {
        // Probe all candidates in parallel
        const fetchPromises = candidates.map(fetchCandidate);

        const settled = await Promise.all(fetchPromises);

        // For OK responses, peek at the body to detect SPA HTML
        const textPromises = settled.map(readCandidateText);

        const texts = await Promise.all(textPromises);

        // Choose the first non-HTML (likely JS) result in candidate order
        let foundUrl = null;
        if (!cancelled) {
          for (let i = 0; i < candidates.length; i += 1) {
            const url = candidates[i];
            const t = texts.find((x) => x.url === url);
            if (t && t?.text) {
              const preview = t.text.trim().slice(0, 20);
              if (preview.startsWith('<')) {
                console.warn('Dummy bundle at', url, 'looks like HTML. Preview:', preview);
              } else {
                foundUrl = url;
                break;
              }
            }
          }
        }

        if (foundUrl) {
          // IMPORTANT: load by direct URL (CSP 'self' allows it)
          await injectScriptSrc(foundUrl);
          if (!cancelled) {
            try {
              await customElements.whenDefined('dummy-wc');
              setLoaded(true);
            } catch (e) {
              setError(e);
            }
          }
          return;
        }

        if (!cancelled) setError(new Error('Could not find a valid dummy-wc bundle in candidates'));
      } catch (err) {
        if (!cancelled) setError(err);
      }
    }

    tryLoad();

    return () => {
      cancelled = true;
    };
  }, []);

  // Render the web component once loaded
  useEffect(() => {
    if (loaded && elRef.current && typeof elRef.current.renderWebComponent === 'function') {
      try {
        elRef.current.renderWebComponent({
          env:
            initiateUiResponse.REACT_APP_ENVIRONMENT === 'development'
              ? 'dev'
              : initiateUiResponse.REACT_APP_ENVIRONMENT,
          lang: language || 'EN',
          source: 'sales',
          Token: accessToken,
          custIdToken: location?.state?.otpToken,
          wcFlow: true,
          dummywc: true,
          salesData: {
            productName,
            productType,
            lang: language || 'EN',
            isEligibilityNotRequire: true,
          },
          analyticsInitialized: true,
        });
      } catch (e) {
        console.error('renderWebComponent call failed', e);
      }
    }
  }, [loaded, productName, productType, language, accessToken, location?.state?.otpToken]);

  return (
    <div style={{ width: '100%', height: '100%' }}>
      {error && (
        <div style={{ padding: 16 }}>
          <strong>Webcomponent load error:</strong>
          <div>{String(error)}</div>
        </div>
      )}
      <div style={{ width: '100%', height: '100%' }}>
        <dummy-wc ref={elRef} />
      </div>
    </div>
  );
}

export default DummyPageInternal;
DummyPageInternal.propTypes = {
  productName: PropTypes.string,
  language: PropTypes.string,
  productType: PropTypes.string,
  accessToken: PropTypes.string,
};
